// Reference Libraries — v2 schema (Module 2 of the Kill Sheet v2 rebuild).
//
// ============================================================================
//  UNIT CONTRACT — read carefully before adding fields.
// ----------------------------------------------------------------------------
//   • All diameters             ........... inches
//   • Weight                    ........... lb/ft
//   • Internal capacity         ........... bbl/ft       (derived: ID² / 1029.4)
//   • External displacement     ........... bbl/ft       (derived: (OD²−ID²)/1029.4)
//   • Strength fields (yield, collapse, …) reference only — NOT used by
//     kill-sheet math unless an explicit downstream wiring is added later.
//   • Metric conversions are derived at render/export time from these
//     canonical units (1 bbl = 0.158987294928 m³, 1 in = 25.4 mm,
//     1 lb/ft = 1.48816 kg/m).
// ============================================================================
//
// Storage model:
//   • Seed dataset      → read-only, loaded from JSON at app startup
//                         (see lib/reflibs/seed.js). Never persisted.
//   • User dataset      → persisted to localStorage["rsc.reflibs.user.v1"]
//                         (see lib/reflibs/storage.js).
//   • Effective view    → seed ∪ user with user IDs overriding seed IDs
//                         (see lib/reflibs/lookup.js).

export const CATEGORIES = [
  "drill_pipe",
  "hwdp",
  "drill_collar",
  "casing",
  "tubing",
  "bop",
  "bit",
  "pump",
  "mud_motor",
  "rotary_steerable",
  "mwd_tool",
  "lwd_tool",
  "stabilizer",
  "crossover",
];

export const CATEGORY_LABELS = {
  drill_pipe:        "Drill Pipe",
  hwdp:              "HWDP",
  drill_collar:      "Drill Collars",
  casing:            "Casing",
  tubing:            "Tubing",
  bop:               "BOP Stacks",
  bit:               "Bits",
  pump:              "Mud Pumps",
  mud_motor:         "Mud Motors",
  rotary_steerable:  "Rotary Steerable",
  mwd_tool:          "MWD Tools",
  lwd_tool:          "LWD Tools",
  stabilizer:        "Stabilizers",
  crossover:         "Crossovers",
};

export const CATEGORY_TAGLINES = {
  drill_pipe:        "Drill pipe specs — API 5DP nominals. Verify against rig tally before use.",
  hwdp:              "Heavy weight drill pipe specs. Tool-joint OD reserved for surge/swab.",
  drill_collar:      "Drill collar specs. Verify against BHA tally.",
  casing:            "Casing specs — API 5CT nominals. Verify against casing tally and manufacturer.",
  tubing:            "Tubing specs — API 5CT nominals. Verify against tubing tally.",
  bop:               "BOP stack reference data. Verify against rig BOP certification sheet.",
  bit:               "Drill bit reference. Verify TFA / nozzle config against bit shop ticket.",
  pump:              "Mud pump model reference. Pump-output is formula-derived; verify against rig pump card.",
  mud_motor:         "Positive-displacement mud motor reference. Verify lobes/stages/bend on the tool ticket.",
  rotary_steerable:  "Rotary-steerable (RSS) tool reference. Verify DLS capability and flow limits with vendor.",
  mwd_tool:          "Measurement-while-drilling tool reference. Verify telemetry & pressure rating with vendor.",
  lwd_tool:          "Logging-while-drilling tool reference. Verify sensor package & flow limits with vendor.",
  stabilizer:        "Stabilizer reference. Verify gauge OD, blade type & through-pipe ID against BHA tally.",
  crossover:         "Crossover sub reference. Verify top/bottom connection makeup torque before use.",
};

const TUBULAR_CATEGORIES = new Set(["drill_pipe","hwdp","drill_collar","casing","tubing"]);
export const BOP_TYPES = ["surface", "subsea"];
export const BIT_TYPES = ["PDC", "Roller Cone", "Hybrid", "Diamond", "Bi-Centre", "Other"];
export const PUMP_TYPES = ["triplex", "duplex"];
export const STABILIZER_BLADE_TYPES = ["integral", "welded", "sleeve", "non-rotating", "Other"];
export const RSS_STEERING_MODES = ["push-the-bit", "point-the-bit", "hybrid", "Other"];
export const MWD_TELEMETRY_TYPES = ["pulse", "EM", "wired-pipe", "Other"];

export const SOURCE_LABELS = {
  rsc_seed_v1:   "Seed",
  user_entered:  "User",
  rig_custom:    "Rig custom",
};

// Canonical empty user record. Used by the editor to bootstrap a "new" form.
export function emptyLibraryRecord(category = "drill_pipe") {
  const base = {
    id: "", category,
    source: "user_entered", notes: "", isActive: true,
    createdAt: null, updatedAt: null,
  };
  if (category === "bop") {
    return { ...base,
      stackName: "", bopType: "surface", nominalSize: "", workingPressure: "",
      ramSlots: "", annularCount: "1", chokeLineId: "", killLineId: "",
      accumulatorGal: "", accumulatorPrechargePsi: "",
    };
  }
  if (category === "bit") {
    return { ...base,
      bitSize: "", bitType: "PDC", iadcCode: "",
      manufacturer: "", model: "", nozzles: "", tfa: "",
    };
  }
  if (category === "pump") {
    return { ...base,
      pumpModel: "", manufacturer: "",
      pumpType: "triplex", linerSize: "", strokeLength: "",
      rodDiameter: "", efficiency: "97", maxPressurePsi: "",
    };
  }
  if (category === "mud_motor") {
    return { ...base,
      model: "", manufacturer: "",
      outerDiameter: "", lengthFt: "",
      lobes: "", stages: "",
      bendDegrees: "0", maxFlowGpm: "", maxRpm: "",
      maxTorqueFtLb: "", differentialPressurePsi: "",
      connection: "",
    };
  }
  if (category === "rotary_steerable") {
    return { ...base,
      model: "", manufacturer: "",
      outerDiameter: "", lengthFt: "",
      steeringMode: "push-the-bit",
      maxDoglegDeg100ft: "", maxFlowGpm: "", maxRpm: "",
      connection: "",
    };
  }
  if (category === "mwd_tool") {
    return { ...base,
      model: "", manufacturer: "",
      outerDiameter: "", innerDiameter: "", lengthFt: "",
      telemetryType: "pulse",
      maxFlowGpm: "", maxPressurePsi: "",
      connection: "",
    };
  }
  if (category === "lwd_tool") {
    return { ...base,
      model: "", manufacturer: "",
      outerDiameter: "", innerDiameter: "", lengthFt: "",
      sensors: "",
      maxFlowGpm: "", maxPressurePsi: "",
      connection: "",
    };
  }
  if (category === "stabilizer") {
    return { ...base,
      model: "", manufacturer: "",
      outerDiameter: "", lengthFt: "",
      bladeType: "integral", blades: "",
      throughPipeId: "",
      connection: "",
    };
  }
  if (category === "crossover") {
    return { ...base,
      model: "",
      topConnection: "", bottomConnection: "",
      stepped: false,
      // Simple-mode fields (also reused as "section 1" when stepped=true if desired)
      outerDiameter: "", innerDiameter: "",
      lengthIn: "",
      // Stepped-mode geometry (only used when stepped=true)
      od1: "", len1: "",
      od2: "", len2: "",
    };
  }
  // Tubular (default)
  return { ...base,
    nominalSize: "", outerDiameter: "", innerDiameter: "",
    weight: "", grade: "", connection: "",
    internalCapacity: null, externalDisplacement: null,
    drift: "", toolJointOd: "", toolJointId: "",
    threadForm: "", couplingOd: "",
    tensileYieldLb: "", torsionalYieldFtLb: "",
    internalYieldPressurePsi: "", collapsePressurePsi: "", minYieldStrengthPsi: "",
  };
}

// ---------------------------------------------------------------------------
// Derived-value helpers — canonical formulas. Mirror the constants used by
// /lib/reference/index.js so the math is identical across the codebase.
// ---------------------------------------------------------------------------
const ID2_DIVISOR = 1029.4;

export function deriveInternalCapacity(innerDiameter) {
  const v = Number(innerDiameter);
  if (!Number.isFinite(v) || v <= 0) return null;
  return (v * v) / ID2_DIVISOR;
}
export function deriveExternalDisplacement(outerDiameter, innerDiameter) {
  const o = Number(outerDiameter);
  const i = Number(innerDiameter);
  if (!Number.isFinite(o) || o <= 0) return null;
  if (!Number.isFinite(i) || i <= 0) return null;
  if (o <= i) return null;
  return (o * o - i * i) / ID2_DIVISOR;
}

// Stamp derived fields onto a tubular record. Non-tubulars pass through.
export function withDerived(rec) {
  if (!rec || !TUBULAR_CATEGORIES.has(rec.category)) return rec;
  const ic = deriveInternalCapacity(rec.innerDiameter);
  const ed = deriveExternalDisplacement(rec.outerDiameter, rec.innerDiameter);
  return {
    ...rec,
    internalCapacity:     ic == null ? null : Number(ic.toFixed(5)),
    externalDisplacement: ed == null ? null : Number(ed.toFixed(5)),
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
const _isPosNum = (v) => v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) > 0;
const _optPosNum = (v) => v === "" || v == null || _isPosNum(v);

/**
 * Validate a v2 library record. Returns { ok, errors }.
 * Dispatches by category — tubular categories run the original API 5DP/5CT
 * geometry validation; bop / bit / pump records run their own validators.
 */
export function validateLibraryRecord(rec) {
  if (!rec || typeof rec !== "object") return { ok: false, errors: ["Record is missing."] };
  if (!CATEGORIES.includes(rec.category))
    return { ok: false, errors: [`Category "${rec.category}" is not a known reference-library category.`] };
  if (rec.category === "bop")               return _validateBop(rec);
  if (rec.category === "bit")               return _validateBit(rec);
  if (rec.category === "pump")              return _validatePump(rec);
  if (rec.category === "mud_motor")         return _validateMudMotor(rec);
  if (rec.category === "rotary_steerable")  return _validateRSS(rec);
  if (rec.category === "mwd_tool")          return _validateMwd(rec);
  if (rec.category === "lwd_tool")          return _validateLwd(rec);
  if (rec.category === "stabilizer")        return _validateStabilizer(rec);
  if (rec.category === "crossover")         return _validateCrossover(rec);
  return _validateTubular(rec);
}

function _validateTubular(rec) {
  const errs = [];
  if (!rec.nominalSize || !String(rec.nominalSize).trim())
    errs.push("Nominal size is required.");

  if (!_isPosNum(rec.outerDiameter))
    errs.push("Outer diameter (in) must be a positive number.");
  if (!_isPosNum(rec.innerDiameter))
    errs.push("Inner diameter (in) must be a positive number.");
  if (_isPosNum(rec.outerDiameter) && _isPosNum(rec.innerDiameter) &&
      Number(rec.outerDiameter) <= Number(rec.innerDiameter))
    errs.push("Outer diameter must be greater than inner diameter.");

  if (!_isPosNum(rec.weight))
    errs.push("Weight (lb/ft) must be a positive number.");
  if (!rec.grade || !String(rec.grade).trim())
    errs.push("Grade is required.");
  if (!rec.connection || !String(rec.connection).trim())
    errs.push("Connection is required.");

  // Optional geometry — numeric if entered.
  if (!_optPosNum(rec.drift))         errs.push("Drift diameter must be a positive number if entered.");
  if (!_optPosNum(rec.toolJointOd))   errs.push("Tool joint OD must be a positive number if entered.");
  if (!_optPosNum(rec.toolJointId))   errs.push("Tool joint ID must be a positive number if entered.");
  if (!_optPosNum(rec.couplingOd))    errs.push("Coupling OD must be a positive number if entered.");

  // Optional drift/tool-joint sanity (DP/HWDP only).
  if (_isPosNum(rec.drift) && _isPosNum(rec.innerDiameter) &&
      Number(rec.drift) > Number(rec.innerDiameter))
    errs.push("Drift diameter cannot exceed inner diameter.");
  if (_isPosNum(rec.toolJointOd) && _isPosNum(rec.outerDiameter) &&
      Number(rec.toolJointOd) < Number(rec.outerDiameter))
    errs.push("Tool joint OD cannot be smaller than body OD.");

  // Strength fields — reference only; numeric-if-entered.
  for (const [k, label] of [
    ["tensileYieldLb",           "Tensile yield"],
    ["torsionalYieldFtLb",       "Torsional yield"],
    ["internalYieldPressurePsi", "Internal yield pressure"],
    ["collapsePressurePsi",      "Collapse pressure"],
    ["minYieldStrengthPsi",      "Minimum yield strength"],
  ]) {
    if (!_optPosNum(rec[k])) errs.push(`${label} must be a positive number if entered.`);
  }

  return { ok: errs.length === 0, errors: errs };
}

// ---- BOP / Bit / Pump validators -----------------------------------------
function _validateBop(rec) {
  const errs = [];
  if (!rec.stackName || !String(rec.stackName).trim()) errs.push("Stack name is required.");
  if (!BOP_TYPES.includes(rec.bopType)) errs.push("BOP type must be Surface or Subsea.");
  if (!rec.nominalSize || !String(rec.nominalSize).trim()) errs.push("Nominal size is required.");
  if (!_isPosNum(rec.workingPressure)) errs.push("Working pressure (psi) must be a positive number.");
  if (!_isPosNum(rec.ramSlots) || !Number.isInteger(Number(rec.ramSlots)) || Number(rec.ramSlots) < 1)
    errs.push("Ram slots must be a positive integer.");
  if (rec.annularCount !== "" && rec.annularCount != null) {
    const n = Number(rec.annularCount);
    if (!Number.isInteger(n) || n < 0) errs.push("Annular count must be a non-negative integer.");
  }
  if (!_optPosNum(rec.chokeLineId)) errs.push("Choke line ID must be a positive number if entered.");
  if (!_optPosNum(rec.killLineId))  errs.push("Kill line ID must be a positive number if entered.");
  if (!_optPosNum(rec.accumulatorGal)) errs.push("Accumulator gal must be a positive number if entered.");
  if (!_optPosNum(rec.accumulatorPrechargePsi)) errs.push("Accumulator precharge must be a positive number if entered.");
  return { ok: errs.length === 0, errors: errs };
}

function _validateBit(rec) {
  const errs = [];
  if (!_isPosNum(rec.bitSize)) errs.push("Bit size (in) must be a positive number.");
  if (!rec.bitType || !String(rec.bitType).trim()) errs.push("Bit type is required.");
  if (rec.nozzles !== "" && rec.nozzles != null) {
    const n = Number(rec.nozzles);
    if (!Number.isInteger(n) || n < 0) errs.push("Nozzle count must be a non-negative integer.");
  }
  if (!_optPosNum(rec.tfa)) errs.push("TFA must be a positive number if entered.");
  return { ok: errs.length === 0, errors: errs };
}

function _validatePump(rec) {
  const errs = [];
  if (!rec.pumpModel || !String(rec.pumpModel).trim()) errs.push("Pump model is required.");
  if (!PUMP_TYPES.includes(rec.pumpType)) errs.push("Pump type must be triplex or duplex.");
  if (!_isPosNum(rec.linerSize))    errs.push("Liner size must be a positive number.");
  if (!_isPosNum(rec.strokeLength)) errs.push("Stroke length must be a positive number.");
  if (!_isPosNum(rec.efficiency) || Number(rec.efficiency) <= 0 || Number(rec.efficiency) > 100)
    errs.push("Efficiency must be a number in (0, 100].");
  if (rec.pumpType === "duplex" && !_isPosNum(rec.rodDiameter))
    errs.push("Rod diameter required for duplex pumps.");
  if (!_optPosNum(rec.maxPressurePsi)) errs.push("Max pressure must be a positive number if entered.");
  return { ok: errs.length === 0, errors: errs };
}


// ---- BHA tools — Mud Motor / RSS / MWD / LWD / Stabilizer / Crossover -----
function _validateMudMotor(rec) {
  const errs = [];
  if (!rec.model || !String(rec.model).trim()) errs.push("Motor model is required.");
  if (!_isPosNum(rec.outerDiameter)) errs.push("Outer diameter (in) must be a positive number.");
  if (!_optPosNum(rec.lengthFt)) errs.push("Length (ft) must be a positive number if entered.");
  if (rec.lobes !== "" && rec.lobes != null) {
    if (!/^\d+\s*[\/:]\s*\d+$/.test(String(rec.lobes).trim()))
      errs.push("Lobe configuration must be in the form A/B (e.g. 5/6, 7/8).");
  }
  if (rec.stages !== "" && rec.stages != null) {
    const n = Number(rec.stages);
    if (!Number.isFinite(n) || n <= 0) errs.push("Stages must be a positive number if entered.");
  }
  if (rec.bendDegrees !== "" && rec.bendDegrees != null) {
    const n = Number(rec.bendDegrees);
    if (!Number.isFinite(n) || n < 0 || n > 5)
      errs.push("Bend (degrees) must be a non-negative number ≤ 5.");
  }
  if (!_optPosNum(rec.maxFlowGpm))    errs.push("Max flow (gpm) must be a positive number if entered.");
  if (!_optPosNum(rec.maxRpm))        errs.push("Max RPM must be a positive number if entered.");
  if (!_optPosNum(rec.maxTorqueFtLb)) errs.push("Max torque (ft·lb) must be a positive number if entered.");
  if (!_optPosNum(rec.differentialPressurePsi))
    errs.push("Differential pressure (psi) must be a positive number if entered.");
  return { ok: errs.length === 0, errors: errs };
}

function _validateRSS(rec) {
  const errs = [];
  if (!rec.model || !String(rec.model).trim()) errs.push("RSS model is required.");
  if (!_isPosNum(rec.outerDiameter)) errs.push("Outer diameter (in) must be a positive number.");
  if (!RSS_STEERING_MODES.includes(rec.steeringMode))
    errs.push("Steering mode must be push-the-bit, point-the-bit, hybrid, or Other.");
  if (!_optPosNum(rec.lengthFt))           errs.push("Length (ft) must be a positive number if entered.");
  if (!_optPosNum(rec.maxDoglegDeg100ft))  errs.push("Max DLS (°/100ft) must be a positive number if entered.");
  if (!_optPosNum(rec.maxFlowGpm))         errs.push("Max flow (gpm) must be a positive number if entered.");
  if (!_optPosNum(rec.maxRpm))             errs.push("Max RPM must be a positive number if entered.");
  return { ok: errs.length === 0, errors: errs };
}

function _validateMwd(rec) {
  const errs = [];
  if (!rec.model || !String(rec.model).trim()) errs.push("MWD model is required.");
  if (!_isPosNum(rec.outerDiameter)) errs.push("Outer diameter (in) must be a positive number.");
  if (!MWD_TELEMETRY_TYPES.includes(rec.telemetryType))
    errs.push("Telemetry type must be pulse, EM, wired-pipe, or Other.");
  if (!_optPosNum(rec.innerDiameter))   errs.push("Inner diameter must be a positive number if entered.");
  if (!_optPosNum(rec.lengthFt))        errs.push("Length (ft) must be a positive number if entered.");
  if (!_optPosNum(rec.maxFlowGpm))      errs.push("Max flow (gpm) must be a positive number if entered.");
  if (!_optPosNum(rec.maxPressurePsi))  errs.push("Max pressure (psi) must be a positive number if entered.");
  return { ok: errs.length === 0, errors: errs };
}

function _validateLwd(rec) {
  const errs = [];
  if (!rec.model || !String(rec.model).trim()) errs.push("LWD model is required.");
  if (!_isPosNum(rec.outerDiameter)) errs.push("Outer diameter (in) must be a positive number.");
  if (!_optPosNum(rec.innerDiameter))  errs.push("Inner diameter must be a positive number if entered.");
  if (!_optPosNum(rec.lengthFt))       errs.push("Length (ft) must be a positive number if entered.");
  if (!_optPosNum(rec.maxFlowGpm))     errs.push("Max flow (gpm) must be a positive number if entered.");
  if (!_optPosNum(rec.maxPressurePsi)) errs.push("Max pressure (psi) must be a positive number if entered.");
  return { ok: errs.length === 0, errors: errs };
}

function _validateStabilizer(rec) {
  const errs = [];
  if (!rec.model || !String(rec.model).trim()) errs.push("Stabilizer name/ID is required.");
  if (!_isPosNum(rec.outerDiameter)) errs.push("Outer diameter (in) must be a positive number.");
  if (!STABILIZER_BLADE_TYPES.includes(rec.bladeType))
    errs.push("Blade type must be one of: integral, welded, sleeve, non-rotating, Other.");
  if (rec.blades !== "" && rec.blades != null) {
    const n = Number(rec.blades);
    if (!Number.isInteger(n) || n < 0) errs.push("Blades must be a non-negative integer.");
  }
  if (!_optPosNum(rec.lengthFt))         errs.push("Length (ft) must be a positive number if entered.");
  if (!_optPosNum(rec.throughPipeId))    errs.push("Through-pipe ID must be a positive number if entered.");
  return { ok: errs.length === 0, errors: errs };
}

function _validateCrossover(rec) {
  const errs = [];
  if (!rec.model || !String(rec.model).trim()) errs.push("Crossover name/ID is required.");
  if (!rec.topConnection || !String(rec.topConnection).trim())
    errs.push("Top connection is required.");
  if (!rec.bottomConnection || !String(rec.bottomConnection).trim())
    errs.push("Bottom connection is required.");

  if (rec.stepped === true) {
    // Stepped geometry — section 1 + section 2 + overall length.
    if (!_isPosNum(rec.od1))  errs.push("OD 1 must be a positive number.");
    if (!_isPosNum(rec.od2))  errs.push("OD 2 must be a positive number.");
    if (!_isPosNum(rec.len1)) errs.push("Length 1 must be a positive number.");
    if (!_isPosNum(rec.len2)) errs.push("Length 2 must be a positive number.");
    if (!_isPosNum(rec.lengthIn)) errs.push("Overall length (in) must be a positive number.");
    if (_isPosNum(rec.len1) && _isPosNum(rec.len2) && _isPosNum(rec.lengthIn) &&
        Number(rec.lengthIn) + 1e-6 < Number(rec.len1) + Number(rec.len2))
      errs.push("Overall length must be ≥ Length 1 + Length 2.");
    if (!_optPosNum(rec.innerDiameter))
      errs.push("Inner diameter must be a positive number if entered.");
    if (_isPosNum(rec.innerDiameter)) {
      if (_isPosNum(rec.od1) && Number(rec.od1) <= Number(rec.innerDiameter))
        errs.push("OD 1 must be greater than inner diameter.");
      if (_isPosNum(rec.od2) && Number(rec.od2) <= Number(rec.innerDiameter))
        errs.push("OD 2 must be greater than inner diameter.");
    }
  } else {
    // Simple (constant-OD) geometry — unchanged contract.
    if (!_optPosNum(rec.outerDiameter)) errs.push("Outer diameter must be a positive number if entered.");
    if (!_optPosNum(rec.innerDiameter)) errs.push("Inner diameter must be a positive number if entered.");
    if (!_optPosNum(rec.lengthIn))      errs.push("Length (in) must be a positive number if entered.");
    if (_isPosNum(rec.outerDiameter) && _isPosNum(rec.innerDiameter) &&
        Number(rec.outerDiameter) <= Number(rec.innerDiameter))
      errs.push("Outer diameter must be greater than inner diameter.");
  }

  return { ok: errs.length === 0, errors: errs };
}
